<template>
  <SettingsPaneV2 header="Performance">
    <template #subheader>View and configure settings for optimizing performance.</template>
    <template #body>
      <div class="body__section performance__new">
        <div class="performance__new__header">Max hardware concurrency</div>
        <div class="performance__new__description">
          Decide the max amount of CPU thread used within the appliance UI.
        </div>
        <div class="performance__new__form">
          <DarwinNumberInput
            v-model="hardwareConcurrencyValue"
            class="worksize"
            :min="1"
            :max="maxValue"
          />
        </div>
      </div>

      <div class="body__section">
        <div class="control__toggle">
          <Toggle
            v-model="neighourTilesValue"
            kind="primary"
            size="small"
          />

          <div class="control__toggle-description">
            <p class="control__toggle-label">Load Neighbour Tiles</p>
            <p class="control__toggle-sub-label">
              Pre-load adjacent tiles to the one in view in tif/svs files
            </p>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <DarwinButton
        size="md"
        kind="primary"
        flair="rounded"
        :disabled="loading"
        @click="onSave"
      >
        Save
      </DarwinButton>
    </template>
  </SettingsPaneV2>
</template>

<script lang="ts">
export default {
  name: 'Performance',
}
</script>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'

import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import Toggle from '@/uiKit/Toggle/Toggle.vue'
import { useToast } from '@/uiKit/Toast/useToast'
import DarwinNumberInput from '@/uiKit/NumberInput/NumberInput.vue'
// eslint-disable-next-line boundaries/element-types
import { useWorkviewStore } from '@/modules/Workview/useWorkviewStore'

import SettingsPaneV2 from './SettingsPaneV2.vue'

const workviewStore = useWorkviewStore()
const toast = useToast()

const loading = ref(false)
const hardwareConcurrencyValue = ref(0)
const neighourTilesValue = ref(false)

const hardwareConcurrency = computed(() => workviewStore.hardwareConcurrency)

const neighbourTiles = computed(() => workviewStore.neighbourTiles)

const maxValue = computed(() =>
  // we strive to always leave the main thread free
  // or at least set concurrency to 1
  Math.max(navigator.hardwareConcurrency - 1, 1),
)

const onSave = (): void => {
  loading.value = true
  workviewStore.setHardwareConcurrency(hardwareConcurrencyValue.value)
  workviewStore.setNeighbourTiles(neighourTilesValue.value)
  toast.success({ meta: { title: 'Settings updated' } })
  loading.value = false
}

onMounted(() => {
  hardwareConcurrencyValue.value = hardwareConcurrency.value
  neighourTilesValue.value = neighbourTiles.value
})
</script>

<style lang="scss" scoped>
.body__section {
  margin-bottom: 16px;
}

.control__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.control__toggle-description {
  flex: 1;
}

.control__toggle-label,
.control__toggle-sub-label {
  @include typographyRegularBody200;
  color: var(--color-text-default);
  margin-left: 8px;
}
.control__toggle-sub-label {
  color: var(--color-text-subtlest);
}

.performance__new {
  @include col;
  border-radius: 16px;
  background-color: var(--color-surface-tertiary);
  padding: 8px;
}

.performance__new__header {
  @include typographyRegularBody200;
  color: var(--color-text-default);
  margin-top: 4px;
  margin-left: 12px;
}

.performance__new__description {
  @include typographyRegularBody200;
  margin-bottom: 4px;
  margin-left: 12px;
  color: var(--color-text-subtlest);
}

.performance__new__form {
  @include row--center;
  border-radius: 14px;
  background-color: var(--color-surface-default);
  gap: 14px;
  margin-top: 8px;
  padding: 16px;
}
</style>
